import SeasonTicket from '@components/season-ticket';
import { formatDateTime } from '@helpers/date';
import { EventSession } from '@types';
import GameTicket from './game-ticket/GameTicket';
import getTicketType from '@utils/tickets/getTicketType';
import ShowTicket from './show-ticket/ShowTicket';

export type RenderTicketsOptions = {
  returnedTicketUuids?: Set<string>;
  onRefundSuccess?: (ticketUuid: string) => void;
};

const renderTickets = (
  eventSession: EventSession,
  orderUuid?: string,
  options?: RenderTicketsOptions
) => {
  const { returnedTicketUuids, onRefundSuccess } = options ?? {};
  const seats = eventSession.seats ?? [];
  if (
    eventSession.consistType === 'SEASON_TICKET' ||
    eventSession.consistType === 'FLEX_PASS'
  ) {
    return seats.map((seat) => {
      const uniqueKey = orderUuid ? `${orderUuid}-${seat.uuid}` : seat.uuid;
      const isReturned = !!(seat.ticketUuid && returnedTicketUuids?.has(seat.ticketUuid));
      return (
        <SeasonTicket
          sector_prefix={seat.locationSchemeSeatsOutgroupPrefix}
          sector={seat.locationSchemeSeatsOutgroupName}
          line_prefix={seat.locationSchemeSeatsIngroupPrefix}
          line={seat.locationSchemeSeatsIngroupName}
          seat_prefix={seat.locationSchemeSeatPrefix}
          seat={seat.locationSchemeSeatName}
          price={seat.price}
          key={uniqueKey}
          eventSessionTypeName={eventSession.eventSessionTypeName ?? eventSession.eventTypeName ?? ''}
          eventTypeName={eventSession.eventTypeName}
          dateEnd={eventSession.dateEnd}
          ticketUuid={seat.ticketUuid}
          uuid={seat.uuid}
          eventSessionId={eventSession.id}
          barcode={seat.ticketBarcode}
          orderUuid={orderUuid ?? seat.orderUuid}
          isReturned={isReturned}
          onRefundSuccess={onRefundSuccess}
        />
      );
    });
  }

  if (eventSession.consistType === 'TICKET') {
    const {
      teamACaption,
      teamBCaption,
      teamALogoLink,
      teamBLogoLink,
      matchdayName,
      seasonName,
      dateBegin,
      logoLink,
      caption,
      eventSessionTypeName,
    } = eventSession;

    const ticketType = getTicketType(eventSession);

    const { date, time } = formatDateTime(dateBegin!);

    if (ticketType === 'game') {
      return seats.map((seat) => {
        const uniqueKey = orderUuid ? `${orderUuid}-${seat.uuid}` : seat.uuid;
        const isReturned = !!(seat.ticketUuid && returnedTicketUuids?.has(seat.ticketUuid));
        return (
          <GameTicket
          key={uniqueKey}
          matchdayName={matchdayName!}
          seasonName={seasonName!}
          teamACaption={teamACaption!}
          teamBCaption={teamBCaption!}
          teamALogoLink={`${teamALogoLink}`}
          teamBLogoLink={`${teamBLogoLink}`}
          sector_prefix={seat.locationSchemeSeatsOutgroupPrefix}
          sector={seat.locationSchemeSeatsOutgroupName}
          line_prefix={seat.locationSchemeSeatsIngroupPrefix}
          line={seat.locationSchemeSeatsIngroupName}
          seat_prefix={seat.locationSchemeSeatPrefix}
          seat={seat.locationSchemeSeatName}
          date={date}
          dateEnd={eventSession.dateEnd}
          time={time}
          price={seat.price}
          ticketUuid={seat.ticketUuid}
          uuid={seat.uuid}
          barcode={seat.ticketBarcode}
          orderUuid={orderUuid ?? seat.orderUuid}
          categoryType={seat.categoryType}
          isReturned={isReturned}
          onRefundSuccess={onRefundSuccess}
          />
        );
      });
    } else if (ticketType === 'show') {
      return seats.map((seat) => {
        const uniqueKey = orderUuid ? `${orderUuid}-${seat.uuid}` : seat.uuid;
        const isReturned = !!(seat.ticketUuid && returnedTicketUuids?.has(seat.ticketUuid));
        return (
          <ShowTicket
          key={uniqueKey}
          sector_prefix={seat.locationSchemeSeatsOutgroupPrefix}
          sector={seat.locationSchemeSeatsOutgroupName}
          line_prefix={seat.locationSchemeSeatsIngroupPrefix}
          line={seat.locationSchemeSeatsIngroupName}
          seat_prefix={seat.locationSchemeSeatPrefix}
          seat={seat.locationSchemeSeatName}
          date={date}
          dateEnd={eventSession.dateEnd}
          time={time}
          price={seat.price}
          logoLink={logoLink ? `${logoLink}` : undefined}
          caption={caption}
          eventSessionTypeName={eventSessionTypeName ?? eventSession.eventTypeName ?? ''}
          ticketUuid={seat.ticketUuid}
          uuid={seat.uuid}
          barcode={seat.ticketBarcode}
          orderUuid={orderUuid ?? seat.orderUuid}
          categoryType={seat.categoryType}
          isReturned={isReturned}
          onRefundSuccess={onRefundSuccess}
          />
        );
      });
    }
    return null;
  }
};

export default renderTickets;
